import { pdf } from '@react-pdf/renderer';
import { createElement, type ReactElement } from 'react';

import { buildPdfFilename } from '../../shared/format.ts';
import { InvoiceDocument } from './InvoiceDocument.tsx';
import type { PdfInvoiceModel } from './model.ts';
import { validatePdfOnePageLayout } from './preflight.ts';
import type { PdfSignature } from './signature.ts';

export const PDF_GENERATION_ERROR = 'Could not generate PDF. Try again or check the logs.';

export interface DownloadInvoicePdfInput {
  invoice: PdfInvoiceModel;
  signature?: PdfSignature | null;
}

export type DownloadInvoicePdfResult =
  | { ok: true; filename: string }
  | { ok: false; error: string };

export interface DownloadInvoicePdfDependencies {
  renderToBlob?: (document: ReactElement) => Promise<Blob>;
  createObjectURL?: (blob: Blob) => string;
  revokeObjectURL?: (url: string) => void;
  createAnchor?: () => HTMLAnchorElement;
  appendAnchor?: (anchor: HTMLAnchorElement) => void;
}

export async function downloadInvoicePdf(
  input: DownloadInvoicePdfInput,
  dependencies: DownloadInvoicePdfDependencies = {},
): Promise<DownloadInvoicePdfResult> {
  const preflight = validatePdfOnePageLayout(input.invoice);
  if (!preflight.ok) return { ok: false, error: preflight.error };

  const deps = resolveDependencies(dependencies);
  let filename: string | null = null;
  let objectUrl: string | null = null;
  let anchor: HTMLAnchorElement | null = null;
  let failed = false;

  try {
    filename = buildPdfFilename(
      { number: input.invoice.number, date: input.invoice.date },
      { name: input.invoice.sender.name },
    );
    const document = createElement(InvoiceDocument, {
      invoice: input.invoice,
      signature: input.signature ?? null,
    });
    const blob = await deps.renderToBlob(document);
    objectUrl = deps.createObjectURL(blob);
    anchor = deps.createAnchor();
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.target = '_blank';
    anchor.style.display = 'none';
    deps.appendAnchor(anchor);
    anchor.click();
  } catch {
    failed = true;
  } finally {
    try {
      anchor?.remove();
    } catch {
      failed = true;
    }

    if (objectUrl) {
      try {
        deps.revokeObjectURL(objectUrl);
      } catch {
        failed = true;
      }
    }
  }

  return failed || filename === null
    ? { ok: false, error: PDF_GENERATION_ERROR }
    : { ok: true, filename };
}

function resolveDependencies(
  dependencies: DownloadInvoicePdfDependencies,
): Required<DownloadInvoicePdfDependencies> {
  return {
    renderToBlob: dependencies.renderToBlob ?? renderDocumentToBlob,
    createObjectURL: dependencies.createObjectURL ?? ((blob) => URL.createObjectURL(blob)),
    revokeObjectURL: dependencies.revokeObjectURL ?? ((url) => URL.revokeObjectURL(url)),
    createAnchor: dependencies.createAnchor ?? (() => document.createElement('a')),
    appendAnchor: dependencies.appendAnchor ?? ((anchor) => document.body.appendChild(anchor)),
  };
}

async function renderDocumentToBlob(document: ReactElement): Promise<Blob> {
  return pdf(document as Exclude<Parameters<typeof pdf>[0], undefined>).toBlob();
}
